import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Users, Eye, FileText, ChevronRight } from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { fetchCoachPathways, deletePathway, updatePathway } from '../../../lib/pathways';
import type { PathwayWithStats } from '../../../types/pathway';

export function CoachPathwaysTR() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [pathways, setPathways] = useState<PathwayWithStats[]>([]);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) { navigate('/tr/login'); return; }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const { data: coach } = await supabase
        .from('coaches')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!coach) { navigate('/tr/coach'); return; }
      setCoachId(coach.id);

      const data = await fetchCoachPathways(coach.id);
      setPathways(data);
    } catch (err) {
      console.error('Error loading pathways:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bu gelisim yolunu silmek istediginizden emin misiniz? Bu islem geri alinamaz.')) return;
    setDeletingId(id);
    try {
      await deletePathway(id);
      setPathways(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting pathway:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePublish = async (pathway: PathwayWithStats) => {
    setTogglingId(pathway.id);
    try {
      await updatePathway(pathway.id, { isPublished: !pathway.isPublished });
      setPathways(prev => prev.map(p =>
        p.id === pathway.id ? { ...p, isPublished: !p.isPublished } : p
      ));
    } catch (err) {
      console.error('Error toggling publish:', err);
    } finally {
      setTogglingId(null);
    }
  };

  if (isLoading) return <LoadingSpinner message="Gelisim yollari yukleniyor..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="freud-heading-lg text-chocolate-900">Gelisim Yollari</h1>
              <p className="text-sm text-chocolate-500">Danisanlariniz icin ogrenme yolculuklari olusturun ve yonetin</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/tr/coach/pathways/new')}
            className="freud-btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Yol
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {pathways.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-12 text-center"
          >
            <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-sage-600" />
            </div>
            <h2 className="freud-heading-xl text-chocolate-900 mb-3">Henuz Gelisim Yolu Yok</h2>
            <p className="freud-text-base text-chocolate-500 mb-8 max-w-md mx-auto">
              Danisanlarinizi yapilandirilmis bir buyume ve gelisim yolculuguna yonlendirmek icin ilk gelisim yolunuzu olusturun.
            </p>
            <button
              onClick={() => navigate('/tr/coach/pathways/new')}
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Ilk Gelisim Yolunuzu Olusturun
            </button>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {pathways.map((pathway, index) => (
              <motion.div
                key={pathway.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="freud-card-elevated p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="freud-heading-base text-chocolate-900 truncate">
                        {pathway.name || 'Isimsiz Yol'}
                      </h3>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                        pathway.isPublished
                          ? 'bg-success-50 text-green-700'
                          : 'bg-cream-200 text-chocolate-500'
                      }`}>
                        {pathway.isPublished ? 'Yayinlandi' : 'Taslak'}
                      </span>
                    </div>
                    {pathway.description && (
                      <p className="text-sm text-chocolate-500 mb-3 line-clamp-2">{pathway.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-chocolate-400">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {pathway.stageCount} {pathway.stageCount === 1 ? 'asama' : 'asama'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {pathway.assignmentCount} {pathway.assignmentCount === 1 ? 'katilimci' : 'katilimci'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleTogglePublish(pathway)}
                      disabled={togglingId === pathway.id}
                      className={`p-2 rounded-lg transition-colors ${
                        pathway.isPublished
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-chocolate-400 hover:bg-cream-100'
                      }`}
                      title={pathway.isPublished ? 'Yayindan Kaldir' : 'Yayinla'}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <Link
                      to={`/tr/coach/pathways/${pathway.id}/edit`}
                      className="p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg transition-colors"
                      title="Duzenle"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(pathway.id)}
                      disabled={deletingId === pathway.id}
                      className="p-2 text-chocolate-400 hover:bg-warm-50 hover:text-warm-600 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <Link
                      to={`/tr/coach/pathways/${pathway.id}/edit`}
                      className="p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
